import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {AsyncThunkConfig, RootState} from "./store";
import {requestsAPI} from "../app/api";
import {AppStatusType} from "./appStatusReducer";
import {RequestsTypes, RequestTopic} from "../types/requestsTypes";

interface InitialStateType {
    requests: RequestsTypes[]
    defaultRequests: RequestsTypes[]
    currentPageMode: 'Status' | 'Pin'
    currentRequest: RequestsTypes | null
    requestsTopics: RequestTopic[]
    requestsStatuses: RequestTopic[]
    requestsIcons: RequestTopic[]
    requestTitle: string | null
    photosForRequest: any[]
    currentPage: number
    districts: {
        id: number,
        name: string,
        description: string}[]
    defaultDistricts: {
        id: number,
        name: string,
        description: string}[]
    quarters: {
        "id": 0,
        "name": "string",
        "description": "string",
        "district": {
            "id": 0,
            "name": "string",
            "description": "string"
        }
    }[]
    selectedDistrict: string
    selectedRegion: string
    isRequestsLoading: boolean
}

const initialState: InitialStateType = {
    requests: [],
    defaultRequests: [],
    currentPageMode: 'Pin',
    currentRequest: null,
    requestsTopics: [],
    requestsStatuses: [],
    requestsIcons: [],
    requestTitle: null,
    photosForRequest: [],
    currentPage: 0,
    districts: [],
    defaultDistricts: [],
    quarters: [],
    selectedRegion: 'Region auswählen',
    selectedDistrict: 'Bezirk auswählen',
    isRequestsLoading: false
}

export const requestsSlice = createSlice({
    name: 'requests',
    initialState,
    reducers: {
        onSetCurrentPageMode: (state, action: PayloadAction<'Status' | 'Pin'>) => {
            state.currentPageMode = action.payload
        },
        onSetGridRows: (state, action: PayloadAction<RequestsTypes[]>) => {
            state.requests = action.payload
        },
        onUpdateRequestField(
            state,
            action: PayloadAction<{ fieldKey: string; value: string | number | boolean }>
        ) {
            const {fieldKey, value} = action.payload;

            function setNestedField(obj: any, path: string, value: any): any {
                const keys = path.replace(/\[(\d+)\]/g, '.$1').split('.');
                const newObj = {...obj};
                let current = newObj;

                for (let i = 0; i < keys.length - 1; i++) {
                    const key = keys[i];
                    current[key] = Array.isArray(current[key])
                        ? [...current[key]]
                        : {...current[key]};
                    current = current[key];
                }

                current[keys[keys.length - 1]] = value;
                return newObj;
            }

            if (state.currentRequest) {
                state.currentRequest = setNestedField(state.currentRequest, fieldKey, value);
                // requestsHelper.setRequestsFields(setNestedField(state.currentRequest, fieldKey, value))
            }
        },
        onSetRequestTitle: (state, action: PayloadAction<string | null>) => {
            state.requestTitle = action.payload
        },
        onSetRequestFieldsData: (state, action: PayloadAction<RequestsTypes>) => {
            state.currentRequest = action.payload
        },
        onSetRequestPhotos: (state, action: PayloadAction<any[]>) => {
            state.photosForRequest = action.payload
        },
        onSetCurrentPage: (state, action: PayloadAction<number>) => {
            state.currentPage = action.payload
        },
        onSetCurrentRegion: (state, action: PayloadAction<string>) => {
            state.selectedRegion = action.payload
        },
        onSetCurrentDistrict: (state, action: PayloadAction<string>) => {
            state.selectedDistrict = action.payload
        },
        onSetDistricts: (state, action: PayloadAction<{
            id: number,
            name: string,
            description: string}[]>) => {
            state.districts = action.payload
        },
        onChangeRowActiveStatus: (state, action: PayloadAction<{ rowId: number, status: boolean }>) => {
            state.requests = state.requests.map((r) => r.id === action.payload.rowId ? {
                ...r,
                is_active: !action.payload.status
            } : r)
        },
        onSetIsRequestsLoading: (state, action: PayloadAction<boolean>) => {
            state.isRequestsLoading = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(GetRequestsList.fulfilled, (state, action) => {
                state.requests = action.payload
                state.defaultRequests = action.payload
            })
            // .addCase(GetRequestById.fulfilled, (state, action) => {
            //     state.currentRequest = action.payload
            // })
            .addCase(GetRequestsTopicsList.fulfilled, (state, action) => {
                state.requestsTopics = action.payload
            })
            .addCase(GetRequestsStatusesList.fulfilled, (state, action) => {
                state.requestsStatuses = action.payload
            })
            .addCase(GetRequestsIconTypesList.fulfilled, (state, action) => {
                state.requestsIcons = action.payload
            })
            .addCase(GetDistricts.fulfilled, (state, action) => {
                state.districts = action.payload
                state.defaultDistricts = action.payload
            })
            .addCase(GetQuarters.fulfilled, (state, action) => {
                state.quarters = action.payload
            })
    }
})

export const {
    onSetCurrentPageMode,
    onSetGridRows,
    onUpdateRequestField,
    onSetRequestTitle,
    onSetRequestPhotos,
    onSetCurrentPage,
    onChangeRowActiveStatus,
    onSetRequestFieldsData,
    onSetDistricts,
    onSetCurrentRegion,
    onSetCurrentDistrict,
    onSetIsRequestsLoading
} = requestsSlice.actions

export const selectRequestsGridRows = (state: RootState): RequestsTypes[] => state.requests.requests
export const selectDefaultRequestsGridRows = (state: RootState): RequestsTypes[] => state.requests.defaultRequests
export const selectCurrentPageMode = (state: RootState): 'Status' | 'Pin' => state.requests.currentPageMode
export const selectCurrentRequest = (state: RootState): RequestsTypes | null => state.requests.currentRequest
export const selectRequestTopics = (state: RootState): RequestTopic[] => state.requests.requestsTopics
export const selectRequestStatuses = (state: RootState): RequestTopic[] => state.requests.requestsStatuses
export const selectRequestIconTypes = (state: RootState): RequestTopic[] => state.requests.requestsIcons
export const selectRequestTitle = (state: RootState): string | null => state.requests.requestTitle
export const selectRequestPhotos = (state: RootState): any[] => state.requests.photosForRequest
export const selectCurrentPage = (state: RootState): number => state.requests.currentPage
export const selectSelectedRegion = (state: RootState): string => state.requests.selectedRegion
export const selectSelectedDistrict = (state: RootState): string => state.requests.selectedDistrict
export const selectDistricts = (state: RootState): {
    id: number,
    name: string,
    description: string}[] => state.requests.districts
export const selectDefaultDistricts = (state: RootState): {
    id: number,
    name: string,
    description: string}[] => state.requests.defaultDistricts
export const selectQuarters = (state: RootState): {
    "id": 0,
    "name": "string",
    "description": "string",
    "district": {
        "id": 0,
        "name": "string",
        "description": "string"
    }
}[] => state.requests.quarters
export const selectIsRequestsLoading = (state: RootState): boolean => state.requests.isRequestsLoading

export const GetRequestsList = createAsyncThunk<RequestsTypes[], void, AsyncThunkConfig>(
    'requests/getRequestsList',
    async (_, thunkAPI) => {
        thunkAPI.dispatch(onSetIsRequestsLoading(true))
        try {
            const {status, data} = await requestsAPI.getMapRequestsList()
            thunkAPI.dispatch(onSetIsRequestsLoading(false))
            if (status === 200 && data) {
                return thunkAPI.fulfillWithValue(data.request_list, {appStatus: AppStatusType.idle})
            } else {
                return thunkAPI.rejectWithValue(data.request_list)
            }

        } catch (error: any) {
            thunkAPI.dispatch(onSetIsRequestsLoading(false))
            return thunkAPI.rejectWithValue(error?.response?.data?.message)
        }
    }
)

export const GetRequestById = createAsyncThunk<RequestsTypes, { requestId: number, withFields: boolean }, AsyncThunkConfig>(
    'requests/getRequestById',
    async (requestData, thunkAPI) => {
        try {
            const {status, data} = await requestsAPI.getRequestById(requestData.requestId)
            if (status === 200 && data) {
                if (requestData.withFields) {
                    thunkAPI.dispatch(onSetRequestTitle(data.title))
                    thunkAPI.dispatch(onSetRequestPhotos(data.photos))
                    thunkAPI.dispatch(onSetRequestFieldsData(data))
                } else {
                    thunkAPI.dispatch(onSetRequestPhotos(data.photos))
                }
                return thunkAPI.fulfillWithValue(data, {appStatus: AppStatusType.idle})
            } else {
                return thunkAPI.rejectWithValue(data)
            }
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error?.response?.data?.message)
        }
    }
)

export const GetRequestsTopicsList = createAsyncThunk<RequestTopic[], void, AsyncThunkConfig>(
    'requests/getRequestsTopicsList',
    async (_, thunkAPI) => {
        try {
            const {status, data} = await requestsAPI.getRequestTopics()
            if (status === 200 && data) {
                return thunkAPI.fulfillWithValue(data.request_topics, {appStatus: AppStatusType.idle})
            } else {
                return thunkAPI.rejectWithValue(data.request_topics)
            }
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error?.response?.data?.message)
        }
    }
)

export const GetRequestsStatusesList = createAsyncThunk<RequestTopic[], void, AsyncThunkConfig>(
    'requests/getRequestsStatusesList',
    async (_, thunkAPI) => {
        try {
            const {status, data} = await requestsAPI.getRequestStatuses()
            if (status === 200 && data) {
                return thunkAPI.fulfillWithValue(data.request_statuses, {appStatus: AppStatusType.idle})
            } else {
                return thunkAPI.rejectWithValue(data.request_statuses)
            }
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error?.response?.data?.message)
        }
    }
)

export const GetRequestsIconTypesList = createAsyncThunk<RequestTopic[], void, AsyncThunkConfig>(
    'requests/getRequestsIconTypesList',
    async (_, thunkAPI) => {
        try {
            const {status, data} = await requestsAPI.getRequestIconTypes()
            if (status === 200 && data) {
                return thunkAPI.fulfillWithValue(data.request_icon_types, {appStatus: AppStatusType.idle})
            } else {
                return thunkAPI.rejectWithValue(data.request_icon_types)
            }
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error?.response?.data?.message)
        }
    }
)

export const GetDistricts = createAsyncThunk<{
    id: number,
    name: string,
    description: string}[], void, AsyncThunkConfig>(
    'requests/getDistricts',
    async (_, thunkAPI) => {
        try {
            const {status, data} = await requestsAPI.getDistricts()
            if (status === 200 && data) {
                return thunkAPI.fulfillWithValue(data.districts, {appStatus: AppStatusType.idle})
            } else {
                return thunkAPI.rejectWithValue(data.districts)
            }
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error?.response?.data?.message)
        }
    }
)

export const GetQuarters = createAsyncThunk<{
    "id": 0,
    "name": "string",
    "description": "string",
    "district": {
        "id": 0,
        "name": "string",
        "description": "string"
    }
}[], void, AsyncThunkConfig>(
    'requests/getQuarters',
    async (_, thunkAPI) => {
        try {
            const {status, data} = await requestsAPI.getQuarters()
            if (status === 200 && data) {
                return thunkAPI.fulfillWithValue(data.quarters, {appStatus: AppStatusType.idle})
            } else {
                return thunkAPI.rejectWithValue(data.quarters)
            }
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error?.response?.data?.message)
        }
    }
)

export default requestsSlice.reducer
